import React, { useContext, useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { QmsDataEditModalContext } from "../_common/QmsDataTable";
import qmsWorkflowActions from "../../../_actions/_qmsActions/qmsWorkflowActions";
import QmsAdminWorkflowEditor from "./QmsAdminWorkflowEditor";
import { ReactFlowProvider } from "reactflow";
import { QMS } from "../../../_constants/actionConstants";
import { getLocalISOString } from "../../../_utils/generalUtils";

export default function QmsAdminModal_EditWorkflow(props) {
  const { divisionId, tabId } = props;
  const dispatch = useDispatch();
  const qmsDataEditModal = useContext(QmsDataEditModalContext);
  const qmsUser = useSelector((state) => state.qms.qmsUser || null);

  const [workflowId, setWorkflowId] = useState(
    qmsDataEditModal.modalState.data && "workflowId" in qmsDataEditModal.modalState.data
      ? qmsDataEditModal.modalState.data.workflowId
      : null
  );
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [formErrors, setFormError] = useState({ name: null, description: null });

  const workflow = useSelector((state) =>
    workflowId ? state.qms.openWorkflows[workflowId] || null : null
  );

  useEffect(
    () => workflowId && dispatch(qmsWorkflowActions.get(workflowId)),
    [workflowId]
  );

  useEffect(() => {
    if (!workflow) return;
    setName(workflow ? workflow.name : "");
    setDescription(workflow ? workflow.description : "");
  }, [workflow]);

  const validateForm = (fieldName) => {
    const defaultMsg = "Required";
    switch (fieldName) {
      case "name":
        setFormError({ ...formErrors, name: name ? null : defaultMsg });
        break;
      case "description":
        setFormError({ ...formErrors, description: description ? null : defaultMsg });
      default:
        break;
    }
  };

  const formIsIncomplete = () =>
    Object.values(formErrors).some((e) => e) || !name || !description;

  const handleBlur = (e) => {
    if (workflow)
      dispatch({
        type: QMS.WORKFLOW.EDIT,
        workflowId: workflow.workflowId,
        fieldsToEdit: { [e.target.name]: e.target.value },
      });
    validateForm(e.target.name);
  };

  const handleClick_add = async (e) => {
    e.preventDefault();
    const workflowData = {
      workflowId: 0,
      divisionId,
      createdByQmsUserId: qmsUser ? qmsUser.qmsUserId : null,
      name,
      description,
      createdOn: getLocalISOString(new Date()),
      nodes: [],
    };

    const newWorkflowId = await dispatch(qmsWorkflowActions.add(workflowData));
    setWorkflowId(newWorkflowId);
    dispatch(qmsWorkflowActions.getAll(divisionId, tabId));
  };

  const handleClick_update = async (e) => {
    e.preventDefault();
    await dispatch(qmsWorkflowActions.update(workflow));
    dispatch(qmsWorkflowActions.getAll(divisionId, tabId));
    qmsDataEditModal.toggleModal();
  };

  const clearOpenWorkflowAndClose = () => {
    workflow && dispatch({ type: QMS.WORKFLOW.CLEAR, workflowId: workflow.workflowId });
    qmsDataEditModal.toggleModal();
  };

  const renderWorkflowInfoFields = () => (
    <div className="card mb-2">
      <div className="card-body p-2">
        <div className="form-row form-group mb-2">
          <label htmlFor="name" className="col-sm-3 col-form-label my-auto">
            <b>Workflow Name:</b>*
          </label>
          <div className="col-sm-5">
            <input
              type="text"
              name="name"
              id="name"
              className="form-control"
              maxLength="50"
              value={name || ""}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleBlur}
            />
            {formErrors.name && (
              <span className="error-small">
                <small>{formErrors.name}</small>
              </span>
            )}
          </div>
        </div>
        <div className="form-row form-group mb-2">
          <label htmlFor="description" className="col-sm-3 col-form-label my-auto">
            <b>Description:</b>*
          </label>
          <div className="col-sm-9">
            <input
              type="text"
              name="description"
              id="description"
              className="form-control"
              value={description || ""}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={handleBlur}
            />
            {formErrors.description && (
              <span className="error-small">
                <small>{formErrors.description}</small>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const closeBtn = (
    <button className="close" onClick={clearOpenWorkflowAndClose}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  const renderWorkflowNodeEdit = () => (
    <ReactFlowProvider>
      <QmsAdminWorkflowEditor
        divisionId={divisionId}
        workflow={workflow}
        onNodeSelect={() => console.log("selected")}
      />
    </ReactFlowProvider>
  );

  return (
    <Modal
      isOpen={qmsDataEditModal.modalState.showModal}
      toggle={clearOpenWorkflowAndClose}
      fade={false}
      size={"xl"}
    >
      <ModalHeader
        toggle={qmsDataEditModal.toggleModal}
        className="bg-info text-white"
        close={closeBtn}
      >
        <FontAwesomeIcon
          icon={qmsDataEditModal.modalState.data || workflow ? "edit" : "plus"}
          className="fa-fw mr-2"
        />
        {qmsDataEditModal.modalState.data || workflow ? `Edit ` : `Add New `}
        QMS Workflow
      </ModalHeader>
      <ModalBody>
        {renderWorkflowInfoFields()}
        {workflow && renderWorkflowNodeEdit()}
      </ModalBody>
      <ModalFooter className="bg-lightgray">
        <div className="form-row form-group mb-0">
          <button
            className="btn btn-primary"
            name="submit"
            onClick={
              qmsDataEditModal.modalState.data || workflow
                ? handleClick_update
                : handleClick_add
            }
            disabled={formIsIncomplete()}
          >
            <b>{qmsDataEditModal.modalState.data || workflow ? "Update" : "Add"}</b>
          </button>
          <button
            className="btn btn-secondary ml-2"
            color="secondary"
            onClick={clearOpenWorkflowAndClose}
          >
            Cancel
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
